import { useRef, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Topic } from '@glyph/protocol-ts';
import { liveStore, pushLiveEvent, selectLive, type DeviceLive } from '../store/liveStore';
import { qk, useApi, useClient, useDeviceId } from './provider';

const LIVE_TOPICS: Topic[] = ['snapshot', 'health', 'telemetry', 'levels', 'presence', 'update_status'];

/** Subscribe (ref-counted by the client) to a topic set and route events through the rAF-coalesced store. */
export function useLiveSubscription(deviceId: string, topics: readonly Topic[]) {
  const client = useClient();
  const key = topics.join(',');
  useEffect(() => {
    if (!key) return;
    return client.subscribe(deviceId, key.split(',') as Topic[], pushLiveEvent);
  }, [client, deviceId, key]);
}

/** Snapshot, telemetry, component health, presence, audio level, update status for one device. */
export function useLive(id?: string): DeviceLive {
  const deviceId = useDeviceId(id);
  useLiveSubscription(deviceId, LIVE_TOPICS);
  const sel = useMemo(() => selectLive(deviceId), [deviceId]);
  return liveStore(sel);
}

/**
 * Composite `status.get` (engine, record, network, cloud, outputs, version …) polled every 5 s and
 * refreshed on every health push — the parts of device state that are not on a topic.
 */
export function useStatus(id?: string) {
  const api = useApi();
  const client = useClient();
  const qc = useQueryClient();
  const deviceId = useDeviceId(id);
  // Through the cloud every poll is a command relayed to the unit (and a row in its command log), so
  // the console asks less often and lets the pushed telemetry / health / levels topics carry the rest.
  const cloud = client.kind === 'cloud';
  const q = useQuery({ queryKey: qk.status(deviceId), queryFn: () => api.status(deviceId), refetchInterval: cloud ? 10_000 : 5_000, enabled: !!deviceId });
  // Pushed health / update events refresh the composite early, but at most once every 2 s: the
  // gateway answers every (re)subscription with a state frame that carries update_status, and an
  // update in progress reports every few hundred ms, which turned this into ~10 status.get/s and
  // 429s for the whole console (bench 2026-10-19). Trailing edge keeps the last event's refresh.
  const lastRefresh = useRef(0);
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!deviceId) return;
    const MIN_MS = 2_000;
    const refresh = () => {
      lastRefresh.current = Date.now();
      pending.current = null;
      void qc.invalidateQueries({ queryKey: qk.status(deviceId) });
    };
    const unsub = client.subscribe(deviceId, cloud ? ['update_status'] : ['health', 'update_status'], () => {
      const wait = MIN_MS - (Date.now() - lastRefresh.current);
      if (wait <= 0) refresh();
      else if (!pending.current) pending.current = setTimeout(refresh, wait);
    });
    return () => {
      unsub();
      if (pending.current) clearTimeout(pending.current);
      pending.current = null;
    };
  }, [client, cloud, deviceId, qc]);
  return q;
}

/** Finals + partial for one device (the transcript). */
export function useCaptions(id?: string) {
  const deviceId = useDeviceId(id);
  useLiveSubscription(deviceId, ['caption_event']);
  const sel = useMemo(() => selectLive(deviceId), [deviceId]);
  return liveStore((s) => sel(s).captions);
}

/** Live MJPEG preview: URL for an <img> when the transport provides one, else the latest WS frame. */
export function usePreview(id?: string, output = 'HDMI-A', enabled = true) {
  const client = useClient();
  const deviceId = useDeviceId(id);
  const mjpeg = client.previewUrl(deviceId, output);
  useLiveSubscription(deviceId, enabled && !mjpeg ? ['preview'] : []);
  const sel = useMemo(() => selectLive(deviceId), [deviceId]);
  const frame = liveStore((s) => sel(s).previewUrl);
  return enabled ? mjpeg ?? frame : null;
}
